import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { 
  Trophy, 
  Star, 
  Award, 
  TrendingUp, 
  Zap,
  Target,
  Users,
  Flame,
  Crown,
  Gift
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const GamificacaoPDI = ({ colaboradorId }) => {
  const [loading, setLoading] = useState(false);
  const [gamificacao, setGamificacao] = useState(null);
  const [ranking, setRanking] = useState([]);

  useEffect(() => {
    if (colaboradorId) {
      loadGamificacao();
      loadRanking();
    }
  }, [colaboradorId]);

  const loadGamificacao = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/pdi-completo/gamificacao/${colaboradorId}`);
      setGamificacao(response.data);
    } catch (error) {
      console.error('Erro ao carregar gamificação:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRanking = async () => {
    try {
      const response = await axios.get(`${API}/api/pdi-completo/ranking`, {
        params: { limit: 10 }
      });
      setRanking(response.data.ranking || []);
    } catch (error) {
      console.error('Erro ao carregar ranking:', error);
    }
  };

  const getBadgeIcon = (badgeId) => {
    const icons = {
      'aprendiz': '🌱',
      'evoluiu': '📈',
      'mentor': '🎓',
      'reflexivo': '💭',
      'destaque': '⭐'
    };
    return icons[badgeId] || '🏅';
  };

  const getNivelColor = (nivel) => {
    if (nivel >= 10) return 'from-purple-500 to-pink-500';
    if (nivel >= 7) return 'from-blue-500 to-purple-500';
    if (nivel >= 4) return 'from-green-500 to-blue-500';
    return 'from-yellow-500 to-orange-500';
  };

  const getPosicaoMedal = (posicao) => {
    if (posicao === 1) return { icon: <Crown className="h-5 w-5" />, color: 'text-yellow-500' };
    if (posicao === 2) return { icon: <Award className="h-5 w-5" />, color: 'text-gray-400' };
    if (posicao === 3) return { icon: <Award className="h-5 w-5" />, color: 'text-orange-600' };
    return { icon: null, color: 'text-gray-600' };
  };

  if (loading || !gamificacao) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <p className="text-gray-500">Carregando dados de gamificação...</p>
        </CardContent>
      </Card>
    );
  }

  const progressoNivel = (gamificacao.experiencia_atual / gamificacao.experiencia_proximo_nivel) * 100;

  return (
    <div className="space-y-6">
      {/* Perfil de Gamificação */}
      <Card className="overflow-hidden">
        <div className={`h-24 bg-gradient-to-r ${getNivelColor(gamificacao.nivel)}`} />
        <CardContent className="-mt-12 relative z-10">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-24 h-24 bg-white rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600">{gamificacao.nivel}</div>
                  <div className="text-xs text-gray-600">Nível</div>
                </div>
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-1">Seu Progresso</h3>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Zap className="h-4 w-4 mr-1 text-yellow-500" />
                    <span className="font-semibold text-gray-900">{gamificacao.pontos_totais}</span> pontos
                  </div>
                  {gamificacao.streak_dias > 0 && (
                    <div className="flex items-center">
                      <Flame className="h-4 w-4 mr-1 text-orange-500" />
                      <span className="font-semibold text-gray-900">{gamificacao.streak_dias}</span> dias
                    </div>
                  )}
                  {gamificacao.posicao_ranking && (
                    <div className="flex items-center">
                      <Trophy className="h-4 w-4 mr-1 text-blue-500" />
                      <span className="font-semibold text-gray-900">#{gamificacao.posicao_ranking}</span> no ranking
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="text-right">
              <div className="text-sm text-gray-600 mb-1">Próximo Nível</div>
              <div className="text-2xl font-bold text-purple-600">{gamificacao.nivel + 1}</div>
            </div>
          </div>

          {/* Barra de Progresso do Nível */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">
                {gamificacao.experiencia_atual} / {gamificacao.experiencia_proximo_nivel} XP
              </span>
              <span className="font-semibold text-purple-600">
                {progressoNivel.toFixed(0)}%
              </span>
            </div>
            <Progress value={progressoNivel} className="h-3" />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Estatísticas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Target className="h-5 w-5 text-blue-600" />
              <span>Estatísticas</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Ações Concluídas</span>
                <span className="text-2xl font-bold text-blue-600">
                  {gamificacao.acoes_concluidas}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Competências Desenvolvidas</span>
                <span className="text-2xl font-bold text-green-600">
                  {gamificacao.competencias_desenvolvidas}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Mentorias Realizadas</span>
                <span className="text-2xl font-bold text-purple-600">
                  {gamificacao.mentorias_realizadas}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Reflexões Escritas</span>
                <span className="text-2xl font-bold text-orange-600">
                  {gamificacao.reflexoes_escritas}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Badges Conquistados */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Award className="h-5 w-5 text-yellow-600" />
              <span>Conquistas ({gamificacao.badges?.length || 0})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {gamificacao.badges && gamificacao.badges.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {gamificacao.badges.map((badge, idx) => (
                  <Card key={idx} className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-200">
                    <CardContent className="p-3 text-center">
                      <div className="text-4xl mb-2">{getBadgeIcon(badge.id)}</div>
                      <div className="font-semibold text-sm">{badge.nome}</div>
                      <div className="text-xs text-gray-600">{badge.descricao}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {new Date(badge.data_conquista).toLocaleDateString('pt-BR')}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Gift className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Nenhuma conquista ainda</p>
                <p className="text-sm text-gray-400 mt-1">
                  Complete ações para desbloquear badges!
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ranking */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Trophy className="h-5 w-5 text-yellow-600" />
            <span>Ranking Geral</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {ranking.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Ranking não disponível</p>
            </div>
          ) : (
            <div className="space-y-2">
              {ranking.map((item) => {
                const medal = getPosicaoMedal(item.posicao);
                const isCurrentUser = item.colaborador_id === colaboradorId;
                
                return (
                  <div
                    key={item.colaborador_id}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      isCurrentUser 
                        ? 'bg-purple-50 border-2 border-purple-200' 
                        : 'bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`flex items-center justify-center w-8 ${medal.color}`}>
                        {medal.icon || (
                          <span className="font-bold text-lg">#{item.posicao}</span>
                        )}
                      </div>
                      <div>
                        <div className={`font-semibold ${isCurrentUser ? 'text-purple-700' : ''}`}>
                          {item.nome}
                          {isCurrentUser && (
                            <Badge className="ml-2 bg-purple-600 text-white text-xs">
                              Você
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-gray-600">
                          Nível {item.nivel} • {item.badges?.length || 0} badges
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center space-x-1">
                        <Zap className="h-4 w-4 text-yellow-500" />
                        <span className="font-bold text-lg">{item.pontos_totais}</span>
                      </div>
                      <div className="text-xs text-gray-600">pontos</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pontos por Mês */}
      {gamificacao.pontuacao_mes > 0 && (
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-blue-600 rounded-full">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
                <div>
                  <div className="text-sm text-gray-600">Pontos este Mês</div>
                  <div className="text-2xl font-bold text-blue-700">
                    {gamificacao.pontuacao_mes} pontos
                  </div>
                </div>
              </div>
              <Button variant="outline" size="sm">
                Ver Detalhes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default GamificacaoPDI;
